import type { TaskLabelView } from '@/lib/api/generated/model';

import { RemovableLabelChip } from './RemovableLabelChip';

export interface TaskLabelRowProps {
  labels: readonly TaskLabelView[];
  /**
   * Narrow the surrounding surface to this label — D7's "the one surface in
   * this feature where a chip does something".
   *
   * Optional, because the create dialog shows this same row over a board it
   * cannot narrow: the task does not exist yet, so there is nothing to filter
   * to. Omitted, each chip becomes the single remove target `RemovableLabelChip`
   * falls back to.
   */
  onNarrow?: (labelId: string) => void;
  /** Take this label off the task. */
  /** Absent when the labels may only be read (spec 189). */
  onRemove?: (labelId: string) => void;
}

/**
 * A task's labels where they are allowed to act.
 *
 * The card's row (`LabelChipRow`) is inert by construction: a card is already a
 * drag handle and a way into the task, and a third target on it makes both
 * unreliable ([D7](../../../../specs/067-task-labels/research.md#d7)). The
 * drawer has room and no competing gestures, so here a chip is a control —
 * "click a label to see everything else like this" is the natural next move
 * from a task you are already reading.
 *
 * Two affordances, deliberately separate and separately named, and both now
 * inside the pill (`RemovableLabelChip`). Clicking the name narrows; the `×`
 * takes the label off the task. Folding both into one target would make an
 * ordinary click ambiguous between "show me more like this" and "I did not mean
 * this label", which are opposite intentions.
 *
 * Where there is nothing to narrow — the create dialog — `onNarrow` is left off
 * and the pill collapses to one target that removes. That is the chip's own
 * rule, not a second one written here.
 *
 * Both live in one row because a reader has one mental list of what a task is
 * about. An earlier arrangement put read-only chips here and the removal inside
 * the picker's list, which made these chips read as output — the obvious
 * gesture, pressing the label you want gone, did nothing at all.
 */
export function TaskLabelRow({
  labels,
  onNarrow,
  onRemove,
}: TaskLabelRowProps) {
  if (labels.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-1.5">
      {labels.map((label) => (
        <li key={label.id} className="inline-flex items-center">
          <RemovableLabelChip
            label={label}
            onNarrow={onNarrow}
            onRemove={onRemove}
          />
        </li>
      ))}
    </ul>
  );
}
